<template>
    <div class="activity-config-compact">
        <!-- 수집 설정 목록 -->
        <div class="section-block">
            <ActivityConfigToolbar
                :organizations="organizations"
                :facilities="facilities"
                :selected-org.sync="selectedOrg"
                :selected-facility.sync="selectedFacility"
                :filter-status.sync="filterStatus"
                :downloading-excel="downloadingExcel"
                @init="openAutoInitModal"
                @excel-download="downloadExcel"
                @add="openConfigModal('create')"
            />

            <ActivityConfigTable
                :configs="paginatedConfigs"
                :loading="loadingConfigs"
                @row-click="showConfigDetail"
                @toggle="toggleStatus"
                @copy="openCopyModal"
                @edit="openConfigModal('edit', $event)"
                @delete="deleteConfig"
            />

            <!-- 페이지네이션 -->
            <TablePagination
                v-if="filteredConfigs.length > 0"
                :total-items="filteredConfigs.length"
                :current-page.sync="currentPage"
                :page-size.sync="pageSize"
            />
        </div>

        <!-- 설정 모달 -->
        <ActivityConfigFormModal
            v-model="showConfigModal"
            :mode="configModalMode"
            :form.sync="configForm"
            :organizations="organizations"
            :facilities="facilities"
            :categories="categories"
            :factors="factors"
            @save="saveConfig($event)"
        />

        <!-- 복사 모달 -->
        <ActivityConfigCopyModal
            v-model="showCopyModal"
            :copying-config="copyingConfig"
            :copy-form="copyForm"
            :organizations="organizations"
            :facilities="facilities"
            @save="copyConfig($event)"
        />

        <!-- 자동 초기화 모달 -->
        <ActivityConfigAutoInitModal
            v-model="showAutoInitModal"
            :initialize-form.sync="initializeForm"
            :organizations="organizations"
            :filtered-org-boundaries="filteredOrgBoundaries"
            :filtered-oper-boundaries="filteredOperBoundaries"
            :can-initialize="canInitialize"
            :loading="initializingConfigs"
            @preview="previewInitialize"
            @execute="executeAutoInitialize"
            @org-change="onOrgChange"
        />

        <!-- 초기화 미리보기 모달 -->
        <ActivityConfigInitializePreviewModal
            v-model="showInitializePreviewModal"
            :preview="initializePreview"
            @execute="onPreviewExecute"
        />

        <!-- 상세 보기 패널 -->
        <ActivityConfigDetailPanel
            :visible="showDetailModal"
            :config="selectedConfigDetail"
            :loading="loadingConfigDetail"
            @close="closeConfigDetail"
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";

import TablePagination from "../../common/TablePagination.vue";
import ActivityConfigDetailPanel from "../views/ActivityConfigDetailPanel.vue";
import ActivityConfigCopyModal from "../modals/ActivityConfigCopyModal.vue";
import ActivityConfigAutoInitModal from "../modals/ActivityConfigAutoInitModal.vue";
import ActivityConfigInitializePreviewModal from "../modals/ActivityConfigInitializePreviewModal.vue";
import ActivityConfigToolbar from "../views/ActivityConfigToolbar.vue";
import ActivityConfigTable from "../views/ActivityConfigTable.vue";
import ActivityConfigFormModal from "../modals/ActivityConfigFormModal.vue";

export default {
    name: "ActivityConfigManager",
    components: {
        TablePagination,
        ActivityConfigDetailPanel,
        ActivityConfigCopyModal,
        ActivityConfigAutoInitModal,
        ActivityConfigInitializePreviewModal,
        ActivityConfigToolbar,
        ActivityConfigTable,
        ActivityConfigFormModal,
    },
    data() {
        return {
            configs: [],
            filteredConfigs: [],
            loadingConfigs: false,

            selectedOrg: null,
            selectedFacility: null,
            filterStatus: null,

            // 페이지네이션
            currentPage: 1,
            pageSize: 10,

            organizations: [],
            facilities: [],
            categories: [],
            initializeOrgBoundaries: [], // 자동 초기화용 조직 경계 목록
            initializeOperBoundaries: [], // 자동 초기화용 운영 경계 목록

            showConfigModal: false,
            configModalMode: "create",
            configForm: {
                orgIdx: null,
                facilityIdx: null,
                categoryIdx: null,
                startMonth: null,
                endMonth: null,
                draftStat: "DRAFT",
                isRequired: 0,
                collectionMethod: "MANUAL",
                dataSource: null,
                reportCycle: "MONTH",
                preferredFuel: null,
                preferredUnit: null,
                factorIdx: null,
                tier: null,
                isAutoCollect: 0,
                collectSchedule: null,
                collectDay: null,
                collectTime: null,
                apiParams: null,
            },

            initializeForm: {
                orgIdx: null,
                orgBoundaryIdx: null,
                operBoundaryIdx: null,
            },
            initializingConfigs: false,

            downloadingExcel: false,

            showCopyModal: false,
            copyForm: {
                orgIdx: null,
                facilityIdx: null,
                startMonth: null,
                endMonth: null,
            },
            copyingConfig: null,

            // 상세 보기
            showDetailModal: false,
            selectedConfigDetail: null,
            loadingConfigDetail: false,

            // 초기화 미리보기
            showInitializePreviewModal: false,
            initializePreview: null,

            // 자동 초기화 모달
            showAutoInitModal: false,

            factors: [],
        };
    },
    computed: {
        canInitialize() {
            return !!(
                this.initializeForm.orgIdx &&
                this.initializeForm.orgBoundaryIdx &&
                this.initializeForm.operBoundaryIdx
            );
        },
        // 선택된 조직에 해당하는 조직 경계 목록
        filteredOrgBoundaries() {
            if (!this.initializeForm.orgIdx) {
                return [];
            }
            return this.initializeOrgBoundaries.filter((boundary) => boundary.orgIdx === this.initializeForm.orgIdx);
        },
        // 선택된 조직에 해당하는 운영 경계 목록 (조직 경계와 독립적)
        filteredOperBoundaries() {
            if (!this.initializeForm.orgIdx) {
                return [];
            }
            return this.initializeOperBoundaries.filter(
                (operBoundary) => operBoundary.orgIdx === this.initializeForm.orgIdx,
            );
        },
        // 페이지네이션 (TablePagination은 자체 계산, 여기서는 slice용만 사용)
        paginationStart() {
            return (this.currentPage - 1) * this.pageSize;
        },
        paginationEnd() {
            return Math.min(this.paginationStart + this.pageSize, this.filteredConfigs.length);
        },
        paginatedConfigs() {
            return this.filteredConfigs.slice(this.paginationStart, this.paginationEnd);
        },
    },
    watch: {
        // 필터가 변경되면 첫 페이지로 이동
        filteredConfigs() {
            this.currentPage = 1;
        },
        // 페이지 크기가 변경되면 첫 페이지로 이동
        pageSize() {
            this.currentPage = 1;
        },
        // 조직이 변경되면 설정 목록 다시 로드
        selectedOrg() {
            this.loadConfigs();
        },
        // 시설 또는 상태 필터가 변경되면 필터링 적용
        selectedFacility() {
            this.filterConfigs();
        },
        filterStatus() {
            this.filterConfigs();
        },
        // 자동 초기화 모달을 닫으면 폼 초기화
        showAutoInitModal(visible) {
            if (!visible) {
                this.initializeForm = {
                    orgIdx: null,
                    orgBoundaryIdx: null,
                    operBoundaryIdx: null,
                };
            }
        },
        // 복사 모달을 닫으면 폼 초기화
        showCopyModal(visible) {
            if (!visible) {
                this.copyForm = {
                    orgIdx: null,
                    facilityIdx: null,
                    startMonth: null,
                    endMonth: null,
                };
                this.copyingConfig = null;
            }
        },
    },
    async mounted() {
        await this.loadInitialData();
        await this.loadConfigs();
    },
    methods: {
        async loadInitialData() {
            try {
                const orgRes = await carbonApi.organization.getOrgs(1, 100);
                this.organizations = orgRes.data.data?.organizations || [];

                const categoryRes = (await carbonApi.operBoundary?.getAllCategories()) || [];
                this.categories = categoryRes.data.data?.categories || [];

                const facilityRes = await carbonApi.utility.getFacilities();
                this.facilities = facilityRes.data.data?.facilities || [];

                const factorRes = await carbonApi.emissionFactor.getFactors();
                this.factors = factorRes.data.data?.factors || [];
            } catch (error) {
                console.error("초기 데이터 로드 실패:", error);
            }
        },

        // 조직 변경 시 조직 경계 로드
        async onOrgChange() {
            // 하위 선택값 초기화
            this.initializeForm.orgBoundaryIdx = null;
            this.initializeForm.operBoundaryIdx = null;
            this.initializeOrgBoundaries = [];
            this.initializeOperBoundaries = [];

            if (!this.initializeForm.orgIdx) {
                return;
            }

            try {
                // 선택된 조직 찾기
                const selectedOrg = this.organizations.find((org) => org.orgIdx === this.initializeForm.orgIdx);
                if (!selectedOrg) {
                    return;
                }

                const orgCode = selectedOrg.orgCode || selectedOrg.orgIdx;

                // 해당 조직의 조직 경계 로드
                try {
                    const boundaryRes = await carbonApi.orgBoundary.getOrgBoundaries(orgCode);
                    this.initializeOrgBoundaries = boundaryRes.data.data?.boundaries || [];
                } catch (error) {
                    console.error("조직 경계 로드 실패:", error);
                    this.initializeOrgBoundaries = [];
                }

                // 해당 조직의 운영 경계 로드
                try {
                    const operBoundaryRes = await carbonApi.operBoundary.getOperBoundaries(orgCode);
                    this.initializeOperBoundaries =
                        operBoundaryRes.data.data?.boundaries || operBoundaryRes.data.data || [];
                } catch (error) {
                    console.error("운영 경계 로드 실패:", error);
                    this.initializeOperBoundaries = [];
                }
            } catch (error) {
                console.error("조직 변경 처리 실패:", error);
            }
        },

        async loadConfigs() {
            this.loadingConfigs = true;
            try {
                const params = {};
                if (this.selectedOrg) params.orgIdx = this.selectedOrg;

                const response = await carbonApi.activityConfig.getConfigs(params);
                this.configs = response.data.data || [];
                this.filterConfigs();
            } catch (error) {
                console.error("설정 목록 로드 실패:", error);
            } finally {
                this.loadingConfigs = false;
            }
        },

        filterConfigs() {
            let filtered = [...this.configs];

            if (this.selectedFacility) {
                // facilityIdx는 최상위 레벨과 facility 객체 안에 모두 있으므로 최상위 레벨 사용
                filtered = filtered.filter((c) => {
                    const facilityIdx = c.facilityIdx || c.facility?.facilityIdx;
                    return facilityIdx === this.selectedFacility;
                });
            }

            if (this.filterStatus !== null && this.filterStatus !== "") {
                // draftStat 기준으로 필터링 (ACTIVE/INACTIVE/DRAFT 구분)
                filtered = filtered.filter((c) => c.draftStat === this.filterStatus);
            }

            this.filteredConfigs = filtered;
        },

        openConfigModal(mode, config) {
            this.configModalMode = mode;
            if (mode === "create") {
                // 기본값으로 현재 월을 설정
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

                this.configForm = {
                    orgIdx: this.selectedOrg,
                    facilityIdx: null,
                    categoryIdx: null,
                    startMonth: currentMonth,
                    endMonth: null,
                    draftStat: "DRAFT",
                    isRequired: 0,
                    collectionMethod: "MANUAL",
                    dataSource: null,
                    reportCycle: "MONTH",
                    preferredFuel: null,
                    preferredUnit: null,
                    factorIdx: null,
                    tier: null,
                    isAutoCollect: 0,
                    collectSchedule: null,
                    collectDay: null,
                    collectTime: null,
                    apiParams: null,
                };
            } else {
                this.configForm = {
                    configIdx: config.configIdx,
                    orgIdx: config.orgIdx,
                    facilityIdx: config.facilityIdx,
                    categoryIdx: config.categoryIdx,
                    startMonth: config.startMonth,
                    endMonth: config.endMonth,
                    draftStat: config.draftStat,
                    isRequired: config.isRequired ?? 0,
                    collectionMethod: config.collectionMethod || "MANUAL",
                    dataSource: config.dataSource || null,
                    reportCycle: config.reportCycle || "MONTH",
                    preferredFuel: config.preferredFuel || null,
                    preferredUnit: config.preferredUnit || null,
                    factorIdx: config.factorIdx ?? null,
                    tier: config.tier || null,
                    isAutoCollect: config.isAutoCollect || 0,
                    collectSchedule: config.collectCycle || config.collectSchedule || null,
                    collectDay: config.collectDay || null,
                    collectTime: config.collectTime || null,
                    apiParams: config.apiParams || null,
                };
            }
            this.showConfigModal = true;
        },

        async saveConfig(formData) {
            try {
                /** activityConfig.validators.js 와 동일한 YYYYMM 정규화 */
                const toYYYYMM = (v) => {
                    if (v == null || v === "") return null;
                    const s = String(v).trim();
                    if (/^\d{6}$/.test(s)) return s;
                    const m = s.match(/^(\d{4})-(\d{1,2})$/);
                    return m ? `${m[1]}${m[2].padStart(2, "0")}` : null;
                };
                const normStart = toYYYYMM(formData.startMonth);
                const normEnd = toYYYYMM(formData.endMonth);
                /** YYYYMM 문자열 비교용 (무기한 종료) */
                const MONTH_OPEN_END = "999912";

                // 기간 검증 (정규화된 월 기준)
                if (normStart && normEnd && normStart > normEnd) {
                    this.$bvToast.toast("시작월이 종료월보다 늦을 수 없습니다.", {
                        variant: "warning",
                        title: "검증 실패",
                    });
                    return;
                }

                // 기간 겹침 경고 (활성 상태로 저장 시)
                if (formData.draftStat === "ACTIVE" && normStart) {
                    // 겹치는 설정 찾기
                    const overlapping = this.configs.filter((c) => {
                        if (this.configModalMode === "edit" && c.configIdx === formData.configIdx) {
                            return false; // 자기 자신 제외
                        }

                        // 동일 조직, 시설, 카테고리
                        if (c.orgIdx !== formData.orgIdx) return false;
                        if ((c.facilityIdx || null) !== (formData.facilityIdx || null)) return false;
                        if (c.categoryIdx !== formData.categoryIdx) return false;
                        if (c.draftStat !== "ACTIVE") return false; // 활성 설정만 체크

                        // 기간 겹침 체크 (YYYYMM 정규화 문자열 비교)
                        const configStart = normStart;
                        const configEnd = normEnd || MONTH_OPEN_END;
                        const existStart = c.startMonth;
                        const existEnd = c.endMonth || MONTH_OPEN_END;

                        // 겹침 조건: (시작 <= 기존종료) && (종료 >= 기존시작)
                        return configStart <= existEnd && configEnd >= existStart;
                    });

                    if (overlapping.length > 0) {
                        const confirmed = await this.$bvModal.msgBoxConfirm(
                            `다음 ${overlapping.length}개의 활성 설정과 기간이 겹칩니다.\n\n` +
                                overlapping
                                    .map((c) => `• ${c.startMonth || "-"} ~ ${c.endMonth || "무기한"}`)
                                    .join("\n") +
                                "\n\n겹치는 기간의 기존 설정은 자동으로 종료월이 조정되거나 비활성화됩니다.\n계속하시겠습니까?",
                            {
                                title: "기간 겹침 경고",
                                okVariant: "warning",
                                okTitle: "계속",
                                cancelTitle: "취소",
                                size: "md",
                            },
                        );

                        if (!confirmed) {
                            return;
                        }
                    }
                }

                // collectSchedule → collectCycle (서버 필드명)
                const apiParamsNormalized = formData.apiParams
                    ? (() => {
                          const ap = formData.apiParams;
                          const ptAddrs =
                              Array.isArray(ap.ptAddrs) && ap.ptAddrs.length
                                  ? ap.ptAddrs.map((a) => String(a).trim()).filter(Boolean)
                                  : ap.ptAddr != null && String(ap.ptAddr).trim() !== ""
                                    ? [String(ap.ptAddr).trim()]
                                    : [];
                          const ptIdxs =
                              Array.isArray(ap.ptIdxs) && ap.ptIdxs.length
                                  ? ap.ptIdxs.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
                                  : ap.ptIdx != null && ap.ptIdx !== "" && Number.isFinite(Number(ap.ptIdx))
                                    ? [Number(ap.ptIdx)]
                                    : [];
                          const ptAddr =
                              ap.ptAddr != null && String(ap.ptAddr).trim() !== ""
                                  ? String(ap.ptAddr).trim()
                                  : ptAddrs[0] ?? null;
                          const ptIdx =
                              ap.ptIdx != null && ap.ptIdx !== "" && Number.isFinite(Number(ap.ptIdx))
                                  ? Number(ap.ptIdx)
                                  : ptIdxs[0] ?? null;
                          return {
                              ...ap,
                              ptAddr,
                              ptIdx,
                              ptAddrs,
                              ptIdxs,
                          };
                      })()
                    : null;

                /** activityConfig.validators.js 에 정의된 바디 키만 전송 (stripUnknown 이외 방어) */
                const ACTIVITY_CONFIG_API_KEYS = [
                    "orgIdx",
                    "facilityIdx",
                    "categoryIdx",
                    "factorIdx",
                    "startMonth",
                    "endMonth",
                    "draftStat",
                    "isRequired",
                    "collectionMethod",
                    "dataSource",
                    "isAutoCollect",
                    "collectCycle",
                    "collectDay",
                    "collectTime",
                    "apiEndpoint",
                    "apiMethod",
                    "apiHeaders",
                    "apiParams",
                    "apiResponsePath",
                    "preferredFuel",
                    "preferredUnit",
                    "tier",
                    "reportCycle",
                    "defaultUnit",
                    "defaultMetadata",
                    "validFrom",
                    "validTo",
                    "remarks",
                ];

                const merged = {
                    ...formData,
                    startMonth: normStart,
                    endMonth: normEnd,
                    collectCycle: formData.collectSchedule ?? null,
                    apiParams: apiParamsNormalized,
                };
                delete merged.collectSchedule;
                delete merged.configIdx;

                const payload = {};
                for (const k of ACTIVITY_CONFIG_API_KEYS) {
                    if (Object.prototype.hasOwnProperty.call(merged, k) && merged[k] !== undefined) {
                        payload[k] = merged[k];
                    }
                }

                // PUT 스키마에 orgIdx / facilityIdx / categoryIdx 없음 → 수정 시 본문에서 제외
                if (this.configModalMode === "edit") {
                    delete payload.orgIdx;
                    delete payload.facilityIdx;
                    delete payload.categoryIdx;
                }

                const configIdxForUrl = formData.configIdx;

                if (this.configModalMode === "create") {
                    let result = await carbonApi.activityConfig.createConfig(payload);
                    if (result.data.success !== true) {
                        const msg = this.getApiErrorMessage(result.data);
                        throw new Error(msg);
                    }
                    this.$bvToast.toast("수집 설정이 생성되었습니다.", { variant: "success", title: "생성 완료" });
                } else {
                    let result = await carbonApi.activityConfig.updateConfig(configIdxForUrl, payload);
                    if (result.data.success !== true) {
                        const msg = this.getApiErrorMessage(result.data);
                        throw new Error(msg);
                    }
                    this.$bvToast.toast("수집 설정이 수정되었습니다.", { variant: "success", title: "수정 완료" });
                }
                this.showConfigModal = false;
                await this.loadConfigs();
            } catch (error) {
                const errorMessage =
                    error.response?.data != null
                        ? this.getApiErrorMessage(error.response.data)
                        : error.message || "설정 저장에 실패했습니다.";
                this.$bvToast.toast(errorMessage, { variant: "danger", title: "저장 실패" });
            }
        },

        /**
         * Carbon Plugin API 에러 응답에서 사용자에게 보여줄 메시지 추출
         * @param {Object} data - response.data (success: false 시 error.details[], error.message 또는 details[], message)
         */
        getApiErrorMessage(data) {
            if (!data || typeof data !== "object") return "설정 저장에 실패했습니다.";
            const err = data.error || data;
            const details = err.details;
            if (Array.isArray(details) && details.length > 0 && details[0].message) {
                return details[0].message;
            }
            if (err.message && typeof err.message === "string") return err.message;
            if (data.message && typeof data.message === "string") return data.message;
            return "설정 저장에 실패했습니다.";
        },

        async deleteConfig(config) {
            const confirm = await this.alertConfirmWarning(`수집 설정을 삭제하시겠습니까?`);
            if (!confirm.value) return;

            try {
                await carbonApi.activityConfig.deleteConfig(config.configIdx);
                this.$bvToast.toast("수집 설정이 삭제되었습니다.", { variant: "success", title: "삭제 완료" });
                await this.loadConfigs();
            } catch (error) {
                console.error("설정 삭제 실패:", error);
                this.$bvToast.toast("설정 삭제에 실패했습니다.", { variant: "danger", title: "삭제 실패" });
            }
        },

        async previewInitialize() {
            const { orgBoundaryIdx, operBoundaryIdx } = this.initializeForm;
            if (!orgBoundaryIdx || !operBoundaryIdx) return;

            try {
                const selectedOrgBoundary = this.filteredOrgBoundaries.find((b) => b.orgBoundaryIdx === orgBoundaryIdx);
                const selectedOperBoundary = this.filteredOperBoundaries.find(
                    (b) => b.operBoundaryIdx === operBoundaryIdx,
                );

                // 백엔드 initialize와 동일: 조직 경계 시설 × 운영 경계 포함 카테고리
                const facilityRes = await carbonApi.orgBoundary.getFacilities(orgBoundaryIdx);
                const categoryRes = await carbonApi.operBoundary.getCategories(operBoundaryIdx);

                const facilities = facilityRes.data?.data?.facilities || facilityRes.data?.facilities || [];
                const categories = (categoryRes.data?.data?.categories || []).filter((c) => c.included);

                this.initializePreview = {
                    orgBoundaryName: selectedOrgBoundary?.orgBoundaryName || "-",
                    operBoundaryName: selectedOperBoundary?.operBoundaryName || "-",
                    facilityCount: facilities.length,
                    categoryCount: categories.length,
                    totalCount: facilities.length * categories.length,
                    facilities: facilities.map((f) => f.facilityName).slice(0, 5),
                    categories: categories.map((c) => c.categoryName).slice(0, 5),
                };
                this.showInitializePreviewModal = true;
            } catch (error) {
                console.error("미리보기 로드 실패:", error);
                this.$bvToast.toast("미리보기를 불러올 수 없습니다.", { variant: "warning", title: "미리보기 실패" });
            }
        },

        async initializeConfigs() {
            this.initializingConfigs = true;
            try {
                const response = await carbonApi.activityConfig.initializeConfigs(this.initializeForm);
                const result = response.data.data || response.data;

                this.$bvToast.toast(`수집 설정이 자동으로 초기화되었습니다. (${result.totalCreated || 0}건 생성)`, {
                    variant: "success",
                    title: "초기화 완료",
                });
                this.initializeForm = {
                    orgIdx: null,
                    orgBoundaryIdx: null,
                    operBoundaryIdx: null,
                };
                this.initializePreview = null;
                this.showInitializePreviewModal = false;
                await this.loadConfigs();
            } catch (error) {
                console.error("자동 초기화 실패:", error);
                const errorMessage = error.response?.data?.message || error.message || "자동 초기화에 실패했습니다.";
                this.$bvToast.toast(errorMessage, { variant: "danger", title: "초기화 실패" });
            } finally {
                this.initializingConfigs = false;
            }
        },

        async toggleStatus(config) {
            const newStatus = config.draftStat === "ACTIVE" ? "INACTIVE" : "ACTIVE";
            const statusLabel = newStatus === "ACTIVE" ? "활성화" : "비활성화";

            try {
                // 기존 updateConfig API 사용 (draftStat만 업데이트)
                await carbonApi.activityConfig.updateConfig(config.configIdx, { draftStat: newStatus });
                this.$bvToast.toast(`설정이 ${statusLabel}되었습니다.`, {
                    variant: "success",
                    title: "상태 변경 완료",
                });
                await this.loadConfigs();
            } catch (error) {
                console.error("상태 변경 실패:", error);
                const errorMessage = error.response?.data?.message || error.message || "상태 변경에 실패했습니다.";
                this.$bvToast.toast(errorMessage, { variant: "danger", title: "상태 변경 실패" });
            }
        },

        openCopyModal(config) {
            this.copyingConfig = config;
            const now = new Date();
            const currentMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
            this.copyForm = {
                orgIdx: config.orgIdx, // 기본값은 동일 조직
                facilityIdx: config.facilityIdx, // 기본값은 동일 시설
                startMonth: currentMonth,
                endMonth: null,
            };
            this.showCopyModal = true;
        },

        async copyConfig(form) {
            const f = form || this.copyForm;
            if (!f.orgIdx) {
                this.$bvToast.toast("대상 조직을 선택해주세요.", { variant: "warning", title: "입력 필요" });
                return;
            }

            if (!f.startMonth) {
                this.$bvToast.toast("시작월을 입력해주세요.", { variant: "warning", title: "입력 필요" });
                return;
            }

            if (f.endMonth && f.startMonth > f.endMonth) {
                this.$bvToast.toast("시작월이 종료월보다 늦을 수 없습니다.", {
                    variant: "warning",
                    title: "검증 실패",
                });
                return;
            }

            try {
                // 기존 createConfig API 사용 (기존 Config 데이터 복사하여 새 Config 생성)
                const newConfigData = {
                    orgIdx: f.orgIdx, // 사용자가 선택한 조직
                    facilityIdx: f.facilityIdx, // 사용자가 선택한 시설
                    categoryIdx: this.copyingConfig.categoryIdx,
                    startMonth: f.startMonth,
                    endMonth: f.endMonth || null,
                    draftStat: "DRAFT", // 복사된 Config는 기본적으로 초안 상태
                    collectionMethod: this.copyingConfig.collectionMethod,
                    dataSource: this.copyingConfig.dataSource,
                    reportCycle: this.copyingConfig.reportCycle || "MONTH",
                    preferredFuel: this.copyingConfig.preferredFuel || null,
                    preferredUnit: this.copyingConfig.preferredUnit || null,
                    isAutoCollect: this.copyingConfig.isAutoCollect || 0,
                    collectCycle: this.copyingConfig.collectCycle || this.copyingConfig.collectSchedule,
                    collectDay: this.copyingConfig.collectDay,
                    collectTime: this.copyingConfig.collectTime,
                    apiEndpoint: this.copyingConfig.apiEndpoint,
                    apiMethod: this.copyingConfig.apiMethod,
                    apiHeaders: this.copyingConfig.apiHeaders,
                    apiParams: this.copyingConfig.apiParams,
                    apiResponsePath: this.copyingConfig.apiResponsePath,
                    defaultDataTier: this.copyingConfig.defaultDataTier,
                    defaultEvidenceType: this.copyingConfig.defaultEvidenceType,
                    defaultUncertaintyPct: this.copyingConfig.defaultUncertaintyPct,
                    remarks: `[복사] ${this.copyingConfig.remarks || ""}`,
                };

                await carbonApi.activityConfig.createConfig(newConfigData);
                this.$bvToast.toast("설정이 복사되었습니다. (초안 상태)", { variant: "success", title: "복사 완료" });
                this.showCopyModal = false;
                await this.loadConfigs();
            } catch (error) {
                console.error("설정 복사 실패:", error);
                const errorMessage = error.response?.data?.message || error.message || "설정 복사에 실패했습니다.";
                this.$bvToast.toast(errorMessage, { variant: "danger", title: "복사 실패" });
            }
        },

        async downloadExcel() {
            this.downloadingExcel = true;
            try {
                const params = {};
                if (this.selectedOrg) params.orgIdx = this.selectedOrg;

                const response = await carbonApi.activityConfig.exportConfigsExcel(params);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `수집설정_${new Date().toISOString().slice(0, 10)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();

                this.$bvToast.toast("Excel 파일이 다운로드되었습니다.", { variant: "success", title: "다운로드 완료" });
            } catch (error) {
                console.error("Excel 다운로드 실패:", error);
                this.$bvToast.toast("Excel 다운로드에 실패했습니다.", { variant: "danger", title: "다운로드 실패" });
            } finally {
                this.downloadingExcel = false;
            }
        },

        async showConfigDetail(config) {
            if (!config?.configIdx) return;
            this.showDetailModal = true;
            this.selectedConfigDetail = null;
            this.loadingConfigDetail = true;
            try {
                const response = await carbonApi.activityConfig.getConfigDetail(config.configIdx);
                const data = response?.data?.data;
                this.selectedConfigDetail = data || null;
            } catch (error) {
                console.error("설정 상세 조회 실패:", error);
                const msg = error.response?.data?.message || error.message || "설정 상세를 불러올 수 없습니다.";
                this.$bvToast.toast(msg, { variant: "danger", title: "조회 실패" });
                this.showDetailModal = false;
            } finally {
                this.loadingConfigDetail = false;
            }
        },
        closeConfigDetail() {
            this.showDetailModal = false;
            this.selectedConfigDetail = null;
            this.loadingConfigDetail = false;
        },
        openAutoInitModal() {
            this.showAutoInitModal = true;
        },
        executeAutoInitialize() {
            this.showAutoInitModal = false;
            this.initializeConfigs();
        },
        onPreviewExecute() {
            this.showInitializePreviewModal = false;
            this.initializeConfigs();
        },
    },
};
</script>

<style scoped>
.activity-config-compact {
    padding: 10px 16px;
}

.section-block {
    margin-bottom: 20px;

    background: #ffffff;
}

@media (max-width: 768px) {
    .activity-config-compact {
        padding: 8px;
    }
}
</style>
